import React from 'react'
import ReactDOM from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.jsx'
import './index.css'
import { registerSW } from 'virtual:pwa-register'

// Apply persisted theme + density before React mounts so there's no
// flash-of-wrong-theme on first paint. The Settings → Appearance panel
// writes these keys; reading early here is a perf optimization, not a hard
// dep — Settings still re-applies on its own.
try {
  const theme = localStorage.getItem('haythix.theme') || 'dark';
  let resolved = theme;
  if (theme === 'auto') {
    resolved = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
  }
  document.documentElement.setAttribute('data-theme', resolved);

  const density = localStorage.getItem('haythix.density') || 'comfortable';
  document.documentElement.setAttribute('data-density', density);

  // When in 'auto', re-resolve when the OS preference changes mid-session.
  if (theme === 'auto' && window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', e => {
      if (localStorage.getItem('haythix.theme') === 'auto') {
        document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
      }
    });
  }
} catch {}

// ─── Service Worker / PWA Update Strategy ────────────────────────────────────
//
// During active development we deploy frequently; an installed PWA that doesn't
// pick up the latest build leads to "Failed to fetch" / stale-UI symptoms. The
// fix is a three-layered freshness strategy:
//
//   1. Periodic update check every 5 minutes while app is open.
//   2. Update check on visibilitychange — every time the user refocuses the
//      PWA, we ask if there's a new build.
//   3. Health check: if /api/health fails repeatedly while the SW is in
//      control, we unregister the SW and hard-reload to recover.
//
// This is aggressive but appropriate for a fast-moving production app. Once
// release cadence settles, the periodic interval can be relaxed.

// Aggressive freshness during fast deploy cadence — once release tempo
// slows we can relax these intervals.
const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;           // 60s (was 5 min)
const HEALTH_CHECK_INTERVAL_MS = 60 * 1000;           // 1 min
const HEALTH_CHECK_FAILURE_THRESHOLD = 3;             // 3 consecutive failures → recover
const HEALTH_URL = `${import.meta.env.VITE_API_URL || '/api'}/public/stats`;
const AUTO_RELOAD_COUNTDOWN_SEC = 10;                 // user-cancellable countdown

// ─── One-time SW migration ──────────────────────────────────────────────────
// Users who registered the old `autoUpdate` service worker get stuck running
// stale in-memory JS forever (SPA navigation uses cached code, hard refresh
// bypasses to fetch fresh). The fix: unregister ALL existing SWs ONCE, clear
// all caches, and reload. After the reload, the new `prompt`-mode SW
// registers fresh and auto-reload toasts work going forward.
//
// Bump SW_MIGRATION_KEY each time we need to force every client back to a
// known-clean state (e.g. after a SW config change). v3 (2026-05): users
// were stuck on stale JS after the Active Sessions ship — even hard refresh
// didn't dislodge the SW because Ctrl+Shift+R doesn't bypass an installed
// service worker. This forces one clean reload on next load.
const SW_MIGRATION_KEY = 'haythix-sw-migration-v3-sessions';
(async function migrateServiceWorkerIfStale() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (localStorage.getItem(SW_MIGRATION_KEY)) return; // already migrated

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    const hadOldSW = regs.length > 0;
    await Promise.all(regs.map(r => r.unregister().catch(() => {})));
    if (window.caches) {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n).catch(() => {})));
    }
    localStorage.setItem(SW_MIGRATION_KEY, new Date().toISOString());
    if (hadOldSW) {
      // Reload once so the new prompt-mode SW can register from a clean slate.
      location.reload();
      return;
    }
  } catch (err) {
    console.warn('SW migration failed (non-fatal):', err);
    localStorage.setItem(SW_MIGRATION_KEY, 'failed-' + Date.now());
  }
})();

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    // Auto-reload after a short cancellable countdown. Previously the toast
    // was dismissable + manual — users would stay on stale code and have to
    // hard-refresh to see new features (e.g. the Arbitrage UI fixes). Now
    // the page reloads itself unless the user explicitly says "wait."
    showAutoReloadToast(() => updateSW(true));
  },
  onOfflineReady() {
    console.log('haythix ready for offline use');
  },
  onRegisterError(err) {
    console.warn('SW registration failed', err);
  },
});

// 1. Periodic update check while open (60s — fast enough that an active user
//    sees a fresh deploy within a minute, low enough that we don't hammer
//    the SW manifest endpoint)
setInterval(() => { updateSW(); }, UPDATE_CHECK_INTERVAL_MS);

// 2. Check on tab focus / visibility change — covers the "PWA was closed for
//    a long time, user reopens it" case where a fresh deploy is waiting.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    updateSW();
  }
});

// 3. Check on every React-Router-style navigation. SPA navigation doesn't
//    trigger a real page load, so without this a user clicking through pages
//    for an hour would never check for updates between the 60s interval ticks.
//    History-API mutations don't fire popstate, so we wrap pushState/replaceState.
const originalPush = history.pushState;
history.pushState = function (...args) {
  const r = originalPush.apply(this, args);
  try { updateSW(); } catch {}
  return r;
};
const originalReplace = history.replaceState;
history.replaceState = function (...args) {
  const r = originalReplace.apply(this, args);
  try { updateSW(); } catch {}
  return r;
};
window.addEventListener('popstate', () => { try { updateSW(); } catch {} });

// 3. SW health watchdog. If the service worker is in control AND we can't
//    reach a known-public endpoint, the SW is likely stuck in a bad state
//    (cached old API base, broken cache logic, etc.). After N consecutive
//    failures we unregister the SW and hard-reload — recovers without
//    requiring the user to navigate DevTools.
let healthCheckFailures = 0;
let healthCheckRecovering = false;

async function healthCheck() {
  // Only watchdog when an SW is controlling the page — otherwise plain
  // network outages would trigger a recovery cycle, which is wrong.
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  if (healthCheckRecovering) return;
  if (!navigator.onLine) return; // skip when offline by design

  try {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 4000);
    const res = await fetch(HEALTH_URL, { signal: ctl.signal, cache: 'no-store' });
    clearTimeout(t);
    if (res.ok) {
      healthCheckFailures = 0;
      return;
    }
    // Anything non-2xx counts toward the threshold (but a 401/403 still proves
    // the SW is passing the request through, so skip those).
    if (res.status >= 400 && res.status < 500) {
      healthCheckFailures = 0;
      return;
    }
    healthCheckFailures++;
  } catch {
    healthCheckFailures++;
  }
  if (healthCheckFailures >= HEALTH_CHECK_FAILURE_THRESHOLD) {
    healthCheckRecovering = true;
    console.warn('PWA: health check failed', HEALTH_CHECK_FAILURE_THRESHOLD, 'times — recovering by unregistering SW');
    await recoverFromStuckServiceWorker();
  }
}
setInterval(healthCheck, HEALTH_CHECK_INTERVAL_MS);

// ─── PWA install prompt capture ──────────────────────────────────────────────
// Chromium browsers fire `beforeinstallprompt` exactly once, before the page
// has had a chance to render. If the user dismisses the auto-banner, we lose
// the event reference. Capturing it here on the window lets the dedicated
// /install-app page trigger install at any time, even days later.
window.__pwaInstallPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window.__pwaInstallPrompt = e;
  // Custom event so the install page can react if it's mounted.
  window.dispatchEvent(new CustomEvent('pwa:installable'));
});
window.addEventListener('appinstalled', () => {
  window.__pwaInstallPrompt = null;
  window.dispatchEvent(new CustomEvent('pwa:installed'));
});

async function recoverFromStuckServiceWorker() {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const reg of regs) await reg.unregister();
    if (window.caches) {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n)));
    }
  } catch (err) {
    console.warn('SW recovery cleanup failed', err);
  }
  // Hard reload so the next page load gets a fresh SW.
  location.reload();
}

// Auto-reload toast — page reloads itself unless the user clicks "Wait."
// Replaces the previous dismissable toast which let users sit on stale code
// indefinitely. The countdown gives a 10s window to abort if mid-form-entry.
function showAutoReloadToast(onReload) {
  if (document.getElementById('pwa-update-toast')) return;

  let secondsLeft = AUTO_RELOAD_COUNTDOWN_SEC;
  let cancelled = false;

  const toast = document.createElement('div');
  toast.id = 'pwa-update-toast';
  toast.style.cssText = `
    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
    background: linear-gradient(135deg, #1a1a1a 0%, #2a1f0a 100%);
    color: #fff; padding: 16px 22px; border-radius: 14px;
    border: 1.5px solid #F4BE37; box-shadow: 0 10px 40px rgba(244,190,55,0.25);
    display: flex; align-items: center; gap: 14px; z-index: 99999;
    font-family: system-ui, -apple-system, sans-serif; font-size: 14px;
    max-width: calc(100vw - 32px);
    animation: slideUp 0.3s ease-out;
  `;
  toast.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:2px;">
      <div style="font-weight:600;color:#F4BE37;">New version available</div>
      <div style="font-size:12px;color:#aaa;">Auto-reloading in <span id="pwa-countdown">${secondsLeft}</span>s to load the latest haythix build…</div>
    </div>
    <button id="pwa-reload-now-btn" style="background:#F4BE37;color:#000;border:none;padding:8px 16px;border-radius:8px;font-weight:700;cursor:pointer;font-size:13px;white-space:nowrap;">Reload now</button>
    <button id="pwa-wait-btn" style="background:transparent;color:#aaa;border:1px solid #555;padding:8px 12px;border-radius:8px;cursor:pointer;font-size:13px;white-space:nowrap;" title="Stay on current version">Wait</button>
  `;
  document.body.appendChild(toast);

  const countdownEl = document.getElementById('pwa-countdown');
  const tick = () => {
    if (cancelled) return;
    secondsLeft--;
    if (countdownEl) countdownEl.textContent = String(Math.max(0, secondsLeft));
    if (secondsLeft <= 0) {
      onReload();
      return;
    }
    setTimeout(tick, 1000);
  };
  setTimeout(tick, 1000);

  document.getElementById('pwa-reload-now-btn').onclick = () => {
    cancelled = true;
    onReload();
  };
  document.getElementById('pwa-wait-btn').onclick = () => {
    cancelled = true;
    toast.remove();
    // User chose to stay — they'll get re-prompted on the next interval tick.
    // We don't block them forever, but we give them this round to finish what
    // they're doing (e.g. typing in a form).
  };
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>,
)
